﻿
import WalletLayout from "~pages/Wallet/components/WalletLayout";
import SenderFormEdit from "./SenderFormEdit";
import CreditInfoMenu from "~pages/Wallet/CreditInfo/components/CreditInfoMenu";
import { useParams } from "react-router-dom";
import { useConfigureFooter } from "~hooks/useHelpers";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { db } from "~/db/db";
import { useGetClienteCarteira } from "~pages/Wallet/hooks/useWallet";
import { buscaEmissor } from "~services/WalletServices";

const offlineBoardMessage = "Função de busca de código emissor fora da carteira não esta disponível no Modo Offline, conecte-se e tente novamente";
const notFoundMessage = "Nenhum código emissor foi encontrado para o documento do recebedor.";
// const lockedFormMessage = "Emissor localizado pelo endpoint a partir do documento do recebedor. Os campos ficam bloqueados e o formulário não pode ser enviado.";

const normalizeDocumento = (value) => String(value ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();

const isTipoEmissor = (cliente) => [1, 2].includes(Number(cliente?.tipo));

const getResponseItems = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (payload?.id) return [payload];
  return [];
};

const getResponseMessage = (payload, fallback) => {
  if (typeof payload?.message === "string" && payload.message.trim()) return payload.message;
  if (typeof payload?.data?.message === "string" && payload.data.message.trim()) return payload.data.message;
  return fallback;
};


const SenderEdit = () => {
  const { parameter: id } = useParams();

  const [cliente, setCliente] = useState();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [resolvedSenderId, setResolvedSenderId] = useState(id);
  const [isFormLocked, setIsFormLocked] = useState(false);

  const shouldFetchFromApi = Boolean(id && navigator.onLine);
  const clienteRequest = useGetClienteCarteira(id, shouldFetchFromApi);

  const searchLocalSenderByDocument = useCallback(async (documento) => {
    const normalizedDocumento = normalizeDocumento(documento);

    if (!normalizedDocumento) return [];

    const localSenders = await db.cliente
      .filter(
        (item) => isTipoEmissor(item) && normalizeDocumento(item?.documento) === normalizedDocumento
      )
      .toArray();

    return localSenders;
  }, []);

  const resolveSenderFromReceiver = useCallback(async (receiverClient) => {
    if (!receiverClient) {
      return { cliente: undefined, message: notFoundMessage, source: "not-found" };
    }

    if (isTipoEmissor(receiverClient)) {
      return { cliente: receiverClient, message: "", source: "direct" };
    }

    const documento = normalizeDocumento(receiverClient?.documento);

    if (!documento) {
      return { cliente: undefined, message: notFoundMessage, source: "invalid-document" };
    }

    // 1. Busca no Dexie (local)
    const localSenders = await searchLocalSenderByDocument(documento);
    if (localSenders.length > 0) {
      return { cliente: localSenders[0], message: "", source: "dexie" };
    }

    // 2. Sem internet e sem resultado local
    if (!navigator.onLine) {
      return { cliente: undefined, message: offlineBoardMessage, source: "offline" };
    }

    // 3. Busca na API
    const response = await buscaEmissor(documento);

    if (response?.isOffline) {
      return { cliente: undefined, message: offlineBoardMessage, source: "offline" };
    }

    const sendersFromApi = getResponseItems(response?.data).filter(isTipoEmissor);

    if (sendersFromApi.length > 0) {
      const [senderFromApi] = sendersFromApi;
      if (senderFromApi?.id) {
        db.cliente.put(senderFromApi).catch(() => null);
      }
      return { cliente: senderFromApi, message: "", source: "api" };
    }

    // 4. Não encontrou em lugar nenhum
    return {
      cliente: undefined,
      message: getResponseMessage(response?.data, notFoundMessage),
      source: "not-found",
    };
  }, [searchLocalSenderByDocument]);

  useEffect(() => {
    let isMounted = true;

    const loadCliente = async () => {
      const clienteId = Number.parseInt(id, 10);

      if (!clienteId) {
        if (!isMounted) return;
        setCliente(undefined);
        setMessage("");
        setIsFormLocked(false);
        setLoading(false);
        return;
      }

      const clienteLocal = await db.cliente.get(clienteId);
      let baseClient = clienteLocal;

      if (navigator.onLine && clienteRequest.data) {
        baseClient = clienteRequest.data;
        db.cliente.put(clienteRequest.data).catch(() => null);
      } else if (!navigator.onLine && !baseClient) {
        if (!isMounted) return;
        setCliente(undefined);
        setMessage(offlineBoardMessage);
        setIsFormLocked(false);
        setLoading(false);
        return;
      } else if (clienteRequest.isError && !baseClient) {
        if (!isMounted) return;
        setCliente(undefined);
        setMessage(notFoundMessage);
        setIsFormLocked(false);
        setLoading(false);
        return;
      } else if (!baseClient && (clienteRequest.isLoading || clienteRequest.isFetching)) {
        if (!isMounted) return;
        setIsFormLocked(false);
        setLoading(true);
        return;
      }

      const cameFromReceiver = !isTipoEmissor(baseClient);
      const resolved = await resolveSenderFromReceiver(baseClient);

      if (!isMounted) return;

      setCliente(resolved.cliente);
      setResolvedSenderId(resolved.cliente?.id ? String(resolved.cliente.id) : id);
      setMessage(resolved.message || "");
      setIsFormLocked(cameFromReceiver && resolved.source === "api");
      setLoading(false);
    };

    loadCliente();

    return () => {
      isMounted = false;
    };
  }, [clienteRequest.data, clienteRequest.isError, clienteRequest.isFetching, clienteRequest.isLoading, id, resolveSenderFromReceiver]);

  useConfigureFooter({ form: true, formId: "novo", submitLabel: "Atualizar", disabled: isFormLocked }, [isFormLocked])

  return (
    <WalletLayout>
      {loading ? (
        <div className="py-6 flex justify-center">
          <Spinner />
        </div>
      ) : message ? (
        <div className="container mt-8">
          <p className="bg-red-100 text-red-700 border border-red-500 px-4 py-2 rounded-md">
            {message}
          </p>
        </div>
      ) : (
        <>
          <CreditInfoMenu id={id} />
          <SenderFormEdit id={resolvedSenderId} cliente={cliente} isFormLocked={isFormLocked} />
        </>
      )}
    </WalletLayout>
  );
}
export default SenderEdit;
